import { __extends } from "tslib";
import { Observable } from "../Misc/observable";
import { Quaternion, Matrix, Vector3 } from "../Maths/math.vector";
import { Color3 } from '../Maths/math.color';
import "../Meshes/Builders/linesBuilder";
import { Mesh } from "../Meshes/mesh";
import { PointerDragBehavior } from "../Behaviors/Meshes/pointerDragBehavior";
import { Gizmo } from "./gizmo";
import { UtilityLayerRenderer } from "../Rendering/utilityLayerRenderer";
import { StandardMaterial } from "../Materials/standardMaterial";
/**
 * Single plane rotation gizmo
 */
var PlaneRotationGizmo = /** @class */ (function (_super) {
    __extends(PlaneRotationGizmo, _super);
    /**
     * Creates a PlaneRotationGizmo
     * @param gizmoLayer The utility layer the gizmo will be added to
     * @param planeNormal The normal of the plane which the gizmo will be able to rotate on
     * @param color The color of the gizmo
     * @param tessellation Amount of tessellation to be used when creating rotation circles
     * @param useEulerRotation Use and update Euler angle instead of quaternion
     * @param thickness display gizmo axis thickness
     */
    function PlaneRotationGizmo(planeNormal, color, gizmoLayer, tessellation, parent, useEulerRotation, thickness) {
        if (color === void 0) { color = Color3.Gray(); }
        if (gizmoLayer === void 0) { gizmoLayer = UtilityLayerRenderer.DefaultUtilityLayer; }
        if (tessellation === void 0) { tessellation = 32; }
        if (parent === void 0) { parent = null; }
        if (useEulerRotation === void 0) { useEulerRotation = false; }
        if (thickness === void 0) { thickness = 1; }
        var _a;
        var _this = _super.call(this, gizmoLayer) || this;
        _this._pointerObserver = null;
        /**
         * Rotation distance in radians that the gizmo will snap to (Default: 0)
         */
        _this.snapDistance = 0;
        /**
         * Event that fires each time the gizmo snaps to a new location.
         * * snapDistance is the the change in distance
         */
        _this.onSnapObservable = new Observable();
        _this._isEnabled = true;
        _this._parent = null;
        _this._dragging = false;
        _this._parent = parent;
        // Create Material
        _this._coloredMaterial = new StandardMaterial("", gizmoLayer.utilityLayerScene);
        _this._coloredMaterial.diffuseColor = color;
        _this._coloredMaterial.specularColor = color.subtract(new Color3(0.1, 0.1, 0.1));
        _this._hoverMaterial = new StandardMaterial("", gizmoLayer.utilityLayerScene);
        _this._hoverMaterial.diffuseColor = Color3.Yellow();
        _this._disableMaterial = new StandardMaterial("", gizmoLayer.utilityLayerScene);
        _this._disableMaterial.diffuseColor = Color3.Gray();
        _this._disableMaterial.alpha = 0.4;
        // Build mesh on root node
        _this._gizmoMesh = new Mesh("", gizmoLayer.utilityLayerScene);
        var _b = _this._createGizmoMesh(_this._gizmoMesh, thickness, tessellation), rotationMesh = _b.rotationMesh, collider = _b.collider;
        // Setup Rotation Circle
        var rotationCirclePaths = [];
        _this._rotationCircle = _this.setupRotationCircle(rotationCirclePaths, _this._gizmoMesh);
        _this._gizmoMesh.lookAt(_this._rootMesh.position.add(planeNormal));
        _this._rootMesh.addChild(_this._gizmoMesh);
        _this._gizmoMesh.scaling.scaleInPlace(1 / 3);
        // Add drag behavior to handle events when the gizmo is dragged
        _this.dragBehavior = new PointerDragBehavior({ dragPlaneNormal: planeNormal });
        _this.dragBehavior.moveAttached = false;
        _this.dragBehavior.maxDragAngle = Math.PI * 9 / 20;
        _this.dragBehavior._useAlternatePickedPointAboveMaxDragAngle = true;
        _this._rootMesh.addBehavior(_this.dragBehavior);
        // Closures for drag logic
        var dragDistance = 0;
        var lastDragPosition = new Vector3();
        var dragPlanePoint = new Vector3();
        var rotationMatrix = new Matrix();
        var planeNormalTowardsCamera = new Vector3();
        var localPlaneNormalTowardsCamera = new Vector3();
        _this.dragBehavior.onDragStartObservable.add(function (e) {
            if (_this.attachedNode) {
                lastDragPosition.copyFrom(e.dragPlanePoint);
                // This is for instantiation location of rotation circle
                var forward = new Vector3(0, 0, 1);
                var direction = _this._rotationCircle.getDirection(forward);
                direction.normalize();
                // Remove Rotation Circle from parent mesh before drag interaction
                _this._gizmoMesh.removeChild(_this._rotationCircle);
                lastDragPosition.copyFrom(e.dragPlanePoint);
                dragPlanePoint = e.dragPlanePoint;
                var origin_1 = _this._rotationCircle.getAbsolutePosition().clone();
                var originalRotationPoint = _this._rotationCircle.getAbsolutePosition().clone().addInPlace(direction);
                var dragStartPoint = e.dragPlanePoint;
                var angle = Vector3.GetAngleBetweenVectors(originalRotationPoint.subtract(origin_1), dragStartPoint.subtract(origin_1), _this._rotationCircle.up);
                _this._rotationCircle.addRotation(0, angle, 0);
                _this._dragging = true;
            }
        });
        _this.dragBehavior.onDragEndObservable.add(function () {
            dragDistance = 0;
            _this.updateRotationCircle(_this._rotationCircle, rotationCirclePaths, dragDistance, dragPlanePoint);
            _this._gizmoMesh.addChild(_this._rotationCircle); // Add rotation circle back to parent mesh after drag behavior
            _this._dragging = false;
        });
        var tmpSnapEvent = { snapDistance: 0 };
        var currentSnapDragDistance = 0;
        var tmpMatrix = new Matrix();
        var amountToRotate = new Quaternion();
        _this.dragBehavior.onDragObservable.add(function (event) {
            if (_this.attachedNode) {
                // Calc angle over full 360 degree (https://stackoverflow.com/questions/43493711/the-angle-between-two-3d-vectors-with-a-result-range-0-360)
                var nodeScale = new Vector3(1, 1, 1);
                var nodeQuaternion = new Quaternion(0, 0, 0, 1);
                var nodeTranslation = new Vector3(0, 0, 0);
                _this.attachedNode.getWorldMatrix().decompose(nodeScale, nodeQuaternion, nodeTranslation);
                var newVector = event.dragPlanePoint.subtract(nodeTranslation).normalize();
                var originalVector = lastDragPosition.subtract(nodeTranslation).normalize();
                var cross = Vector3.Cross(newVector, originalVector);
                var dot = Vector3.Dot(newVector, originalVector);
                var angle = Math.atan2(cross.length(), dot);
                planeNormalTowardsCamera.copyFrom(planeNormal);
                localPlaneNormalTowardsCamera.copyFrom(planeNormal);
                if (_this.updateGizmoRotationToMatchAttachedMesh) {
                    nodeQuaternion.toRotationMatrix(rotationMatrix);
                    localPlaneNormalTowardsCamera = Vector3.TransformCoordinates(planeNormalTowardsCamera, rotationMatrix);
                }
                // Flip up vector depending on which side the camera is on
                var cameraFlipped = false;
                if (gizmoLayer.utilityLayerScene.activeCamera) {
                    var camVec = gizmoLayer.utilityLayerScene.activeCamera.position.subtract(nodeTranslation);
                    if (Vector3.Dot(camVec, localPlaneNormalTowardsCamera) > 0) {
                        planeNormalTowardsCamera.scaleInPlace(-1);
                        localPlaneNormalTowardsCamera.scaleInPlace(-1);
                        cameraFlipped = true;
                    }
                }
                var halfCircleSide = Vector3.Dot(localPlaneNormalTowardsCamera, cross) > 0.0;
                if (halfCircleSide) {
                    angle = -angle;
                }
                // Snapping logic
                var snapped = false;
                if (_this.snapDistance != 0) {
                    currentSnapDragDistance += angle;
                    if (Math.abs(currentSnapDragDistance) > _this.snapDistance) {
                        var dragSteps = Math.floor(Math.abs(currentSnapDragDistance) / _this.snapDistance);
                        if (currentSnapDragDistance < 0) {
                            dragSteps *= -1;
                        }
                        currentSnapDragDistance = currentSnapDragDistance % _this.snapDistance;
                        angle = _this.snapDistance * dragSteps;
                        snapped = true;
                    }
                    else {
                        angle = 0;
                    }
                }
                dragDistance += cameraFlipped ? -angle : angle;
                _this.updateRotationCircle(_this._rotationCircle, rotationCirclePaths, dragDistance, dragPlanePoint);
                // Convert angle and axis to quaternion (http://www.euclideanspace.com/maths/geometry/rotations/conversions/angleToQuaternion/index.htm)
                var quaternionCoefficient = Math.sin(angle / 2);
                amountToRotate.set(planeNormalTowardsCamera.x * quaternionCoefficient, planeNormalTowardsCamera.y * quaternionCoefficient, planeNormalTowardsCamera.z * quaternionCoefficient, Math.cos(angle / 2));
                // If the meshes local scale is inverted (eg. loaded gltf file parent with z scale of -1) the rotation needs to be inverted on the y axis
                if (tmpMatrix.determinant() > 0) {
                    var tmpVector = new Vector3();
                    amountToRotate.toEulerAnglesToRef(tmpVector);
                    Quaternion.RotationYawPitchRollToRef(tmpVector.y, -tmpVector.x, -tmpVector.z, amountToRotate);
                }
                if (_this.updateGizmoRotationToMatchAttachedMesh) {
                    // Rotate selected mesh quaternion over fixed axis
                    nodeQuaternion.multiplyToRef(amountToRotate, nodeQuaternion);
                }
                else {
                    // Rotate selected mesh quaternion over rotated axis
                    amountToRotate.multiplyToRef(nodeQuaternion, nodeQuaternion);
                }
                // recompose matrix
                _this.attachedNode.getWorldMatrix().copyFrom(Matrix.Compose(nodeScale, nodeQuaternion, nodeTranslation));
                lastDragPosition.copyFrom(event.dragPlanePoint);
                if (snapped) {
                    tmpSnapEvent.snapDistance = angle;
                    _this.onSnapObservable.notifyObservers(tmpSnapEvent);
                }
                _this._matrixChanged();
            }
        });
        var light = gizmoLayer._getSharedGizmoLight();
        light.includedOnlyMeshes = light.includedOnlyMeshes.concat(_this._rootMesh.getChildMeshes(false));
        var cache = {
            colliderMeshes: [collider],
            gizmoMeshes: [rotationMesh],
            material: _this._coloredMaterial,
            hoverMaterial: _this._hoverMaterial,
            disableMaterial: _this._disableMaterial,
            active: false
        };
        (_a = _this._parent) === null || _a === void 0 ? void 0 : _a.addToAxisCache(_this._gizmoMesh, cache);
        _this._pointerObserver = gizmoLayer.utilityLayerScene.onPointerObservable.add(function (pointerInfo) {
            var _a;
            if (_this._customMeshSet) {
                return;
            }
            _this._isHovered = !!(cache.colliderMeshes.indexOf((_a = pointerInfo === null || pointerInfo === void 0 ? void 0 : pointerInfo.pickInfo) === null || _a === void 0 ? void 0 : _a.pickedMesh) != -1);
            if (!_this._parent) {
                var material = _this._isHovered || _this._dragging ? _this._hoverMaterial : _this._coloredMaterial;
                cache.gizmoMeshes.forEach(function (m) {
                    m.material = material;
                    if (m.color) {
                        m.color = material.diffuseColor;
                    }
                });
            }
        });
        return _this;
    }
    /** Create Geometry for Gizmo */
    PlaneRotationGizmo.prototype._createGizmoMesh = function (parentMesh, thickness, tessellation) {
        var collider = Mesh.CreateTorus("ignore", 0.6, 0.03 * thickness, tessellation, this.gizmoLayer.utilityLayerScene);
        collider.visibility = 0;
        var rotationMesh = Mesh.CreateTorus("", 0.6, 0.005 * thickness, tessellation, this.gizmoLayer.utilityLayerScene);
        rotationMesh.material = this._coloredMaterial;
        // Position arrow pointing in its drag axis
        rotationMesh.rotation.x = Math.PI / 2;
        collider.rotation.x = Math.PI / 2;
        parentMesh.addChild(rotationMesh);
        parentMesh.addChild(collider);
        return { rotationMesh: rotationMesh, collider: collider };
    };
    PlaneRotationGizmo.prototype._attachedNodeChanged = function (value) {
        if (this.dragBehavior) {
            this.dragBehavior.enabled = value ? true : false;
        }
    };
    PlaneRotationGizmo.prototype.setupRotationCircle = function (paths, parentMesh) {
        var fillRadians = 0;
        var step = PlaneRotationGizmo._CircleConstants.pi2 / PlaneRotationGizmo._CircleConstants.tessellation;
        for (var p = -Math.PI / 2; p < Math.PI / 2 - 1.5; p += step / 2) {
            var path = [];
            for (var i = 0; i < PlaneRotationGizmo._CircleConstants.pi2 * PlaneRotationGizmo._CircleConstants.rotationCircleRange + 0.01; i += step) {
                if (i < fillRadians) {
                    var x = PlaneRotationGizmo._CircleConstants.radius * Math.sin(i) * Math.cos(p);
                    var z = PlaneRotationGizmo._CircleConstants.radius * Math.cos(i) * Math.cos(p);
                    var y = 0;
                    path.push(new Vector3(x, y, z));
                }
                else {
                    path.push(new Vector3(0, 0, 0));
                }
            }
            paths.push(path);
        }
        var mat = new StandardMaterial("", this.gizmoLayer.utilityLayerScene);
        mat.diffuseColor = Color3.Yellow();
        mat.backFaceCulling = false;
        var mesh = Mesh.CreateRibbon("rotationCircle", paths, false, false, 0, this.gizmoLayer.utilityLayerScene, true);
        mesh.material = mat;
        mesh.material.alpha = .25;
        mesh.rotation.x = Math.PI / 2;
        parentMesh.addChild(mesh);
        return mesh;
    };
    PlaneRotationGizmo.prototype.updateRotationPath = function (pathArr, newFill) {
        // To update the Ribbon, you have to mutate the pathArray in-place
        var step = PlaneRotationGizmo._CircleConstants.pi2 / PlaneRotationGizmo._CircleConstants.tessellation;
        var tessellationCounter = 0;
        for (var p = -Math.PI / 2; p < Math.PI / 2 - 1.5; p += step / 2) {
            var path = pathArr[tessellationCounter];
            if (path) {
                var radianCounter = 0;
                for (var i = 0; i < PlaneRotationGizmo._CircleConstants.pi2 * PlaneRotationGizmo._CircleConstants.rotationCircleRange + 0.01; i += step) {
                    if (path[radianCounter]) {
                        if (i < Math.abs(newFill)) {
                            var absI = (newFill > 0) ? i : i * -1;
                            var absP = (newFill > 0) ? p : p * -1;
                            path[radianCounter].set(PlaneRotationGizmo._CircleConstants.radius * Math.sin(absI) * Math.cos(absP), 0, PlaneRotationGizmo._CircleConstants.radius * Math.cos(absI) * Math.cos(absP));
                        }
                        else {
                            path[radianCounter].set(0, 0, 0);
                        }
                    }
                    radianCounter++;
                }
            }
            tessellationCounter++;
        }
    };
    PlaneRotationGizmo.prototype.updateRotationCircle = function (mesh, paths, newFill, dragPlanePoint) {
        this.updateRotationPath(paths, newFill);
        Mesh.CreateRibbon("rotationCircle", paths, false, false, 0, this.gizmoLayer.utilityLayerScene, undefined, undefined, mesh.geometry ? mesh : undefined);
    };
    Object.defineProperty(PlaneRotationGizmo.prototype, "isEnabled", {
        get: function () {
            return this._isEnabled;
        },
        /**
             * If the gizmo is enabled
             */
        set: function (value) {
            this._isEnabled = value;
            if (!value) {
                this.attachedMesh = null;
            }
            else {
                if (this._parent) {
                    this.attachedMesh = this._parent.attachedMesh;
                }
            }
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Disposes of the gizmo
     */
    PlaneRotationGizmo.prototype.dispose = function () {
        this.onSnapObservable.clear();
        this.gizmoLayer.utilityLayerScene.onPointerObservable.remove(this._pointerObserver);
        this.dragBehavior.detach();
        if (this._gizmoMesh) {
            this._gizmoMesh.dispose();
        }
        if (this._rotationCircle) {
            this._rotationCircle.dispose();
        }
        [this._coloredMaterial, this._hoverMaterial, this._disableMaterial].forEach(function (matl) {
            if (matl) {
                matl.dispose();
            }
        });
        _super.prototype.dispose.call(this);
    };
    PlaneRotationGizmo._CircleConstants = {
        radius: 0.3,
        pi2: Math.PI * 2,
        tessellation: 70,
        rotationCircleRange: 4
    };
    return PlaneRotationGizmo;
}(Gizmo));
export { PlaneRotationGizmo };
//# sourceMappingURL=planeRotationGizmo.js.map